import React, { useEffect, useLayoutEffect, useState } from "react";

import {
  Layout,
  Card,
  Table,
  Space,
  Button,
  Input,
  Switch,
  Popconfirm,
  Modal,
} from "antd";
import dayjs from "dayjs";

import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { displayMessage, getAPI, postAPI } from "../../utils/common";

import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";

import {
  FESTIVALS,
  DELETE_FESTIVAL,
  GENERATE_FESTIVAL,
} from "../../constants/api";

import FestivalModal from "./FestivalModal";
import { PASSCODE } from "../../constants/constent";

function FestivalTable() {
  const { Content } = Layout;

  const [state, setState] = useState([]);

  const [filteredState, setFilteredState] = useState([]);

  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [editData, setEditData] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [passwordModal, setPasswordModal] = useState(false);
  const [passcode, setPasscode] = useState("");

  useLayoutEffect(() => {
    getList();
  }, []);

  const verifyAndGenerate = () => {
    if (passcode !== PASSCODE) {
      displayMessage(ERROR_MSG_TYPE, "Invalid passcode");
      return;
    }
    setLoading(true);
    setPasswordModal(false);

    const successFn = (res) => {
      if (res.status === true) {
        displayMessage(SUCCESS_MESSAGE, res.message);
        getList();
      } else {
        displayMessage(ERROR_MSG_TYPE, res.message);
      }
      setLoading(false);
    };

    const errorFn = () => {
      displayMessage(ERROR_MSG_TYPE, "Unable to generate festival");
      setLoading(false);
    };

    getAPI(GENERATE_FESTIVAL, successFn, errorFn);
  };

  /*
  |--------------------------------------------------------------------------
  | Get Festival List
  |--------------------------------------------------------------------------
  */

  const getList = (page = 1, limit = 10) => {
    setLoading(true);

    const data = new FormData();

    data.append("page", page);

    data.append("limit", limit);

    if (searchText) {
      data.append("name", searchText);
    }

    const successFn = (res) => {
      if (res.status === true) {
        const result = res.data;

        setState(result.data);
        setFilteredState(result.data);

        setPagination({
          current: result.current_page,
          pageSize: result.per_page,
          total: result.total,
        });
      } else {
        setState([]);

        setFilteredState([]);
      }

      setLoading(false);
    };

    const errorFn = () => {
      setLoading(false);

      displayMessage(ERROR_MSG_TYPE, "Unable to get festivals");
    };

    postAPI(FESTIVALS, data, successFn, errorFn);
  };

  /*
  |--------------------------------------------------------------------------
  | Search
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const timer = setTimeout(() => {
      getList(1, pagination.pageSize);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);
  /*
  |--------------------------------------------------------------------------
  | Delete
  |--------------------------------------------------------------------------
  */

  const deleteData = (id) => {
    setLoading(true);

    const data = new FormData();

    data.append("id", id);

    const successFn = (res) => {
      if (res.status === true) {
        displayMessage(SUCCESS_MESSAGE, res.message);

        getList();
      } else {
        setLoading(false);

        displayMessage(ERROR_MSG_TYPE, res.message);
      }
    };

    const errorFn = () => {
      setLoading(false);

      displayMessage(ERROR_MSG_TYPE, "Unable to delete festival");
    };

    postAPI(DELETE_FESTIVAL, data, successFn, errorFn);
  };

  /*
  |--------------------------------------------------------------------------
  | Modal
  |--------------------------------------------------------------------------
  */

  const openAddModal = () => {
    setEditData(null);

    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditData(record);

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditData(null);

    setIsModalOpen(false);
  };

  const afterSave = () => {
    closeModal();

    getList();
  };

  /*
  |--------------------------------------------------------------------------
  | Columns
  |--------------------------------------------------------------------------
  */

  const columns = [
    {
      title: "Sno",
      dataIndex: "ID",
      width: 70,
    },

    {
      title: "Festival Name",
      dataIndex: "Name",
      width: 180,
    },

    {
      title: "Start Date",
      dataIndex: "StartDate",
      width: 150,
    },

    {
      title: "End Date",
      dataIndex: "EndDate",
      width: 150,
    },

    {
      title: "Impact Score",
      dataIndex: "ImpactScore",
      width: 120,
    },

    {
      title: "Status",
      dataIndex: "Status",
      width: 120,
    },

    {
      title: "Action",
      dataIndex: "Action",
      width: 180,
    },
  ];

  const tableData = filteredState.map((row, index) => ({
    key: row.id,

    ID: index + 1,

    Name: row.name,

    StartDate: dayjs(row.start_date).format("DD MMM YYYY"),

    EndDate: dayjs(row.end_date).format("DD MMM YYYY"),

    ImpactScore: row.impact_score,

    Status: <Switch checked={row.status} disabled />,

    Action: (
      <Space>
        <Button
          type="primary"
          ghost
          icon={<EditOutlined />}
          onClick={() => openEditModal(row)}
        >
          Edit
        </Button>

        <Popconfirm
          title="Delete Festival"
          description="Are you sure to delete this festival?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => deleteData(row.id)}
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      </Space>
    ),
  }));
  return (
    <Layout>
      <LayoutHeader />

      <Content>
        <Card
          title="Manage Festivals"
          extra={
            <Space>
              <Input
                placeholder="Search festival"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  width: 250,
                }}
              />

              <Button
                loading={loading}
                disabled={loading}
                type="primary"
                onClick={openAddModal}
              >
                Add <PlusOutlined />
              </Button>
              <Button
                type="primary"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  setPasscode("");
                  setPasswordModal(true);
                }}
              >
                Auto Generate {dayjs().year()}
              </Button>
            </Space>
          }
        >
          <Table
            disabled={loading}
            loading={loading}
            columns={columns}
            dataSource={tableData}
            rowKey="id"
            pagination={pagination}
            onChange={(page) => {
              getList(
                page.current,
                page.pageSize,
              );
            }}
          />
        </Card>
      </Content>

      <LayoutFooter />

      <FestivalModal
        open={isModalOpen}
        close={closeModal}
        editData={editData}
        afterSave={afterSave}
      />

      <Modal
        title="Enter Passcode"
        open={passwordModal}
        onCancel={() => setPasswordModal(false)}
        onOk={verifyAndGenerate}
        okText="Generate"
        centered
        width={400}
        styles={{
          body: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "30px",
          },
        }}
      >
        <Input.OTP
          length={6}
          value={passcode}
          onChange={(value) => setPasscode(value)}
        />
      </Modal>
    </Layout>
  );
}

export default FestivalTable;
